import React from 'react';
import type { AdminLanguage } from '../../services/i18nService';
import { needsAttention, percentApproved, type RowCounts } from '../../utils/translationRows';
import { LANGUAGE_STATUS_LABELS } from './LanguageOverview';
import { isAssignedTo, joinNames } from './roles';
import styles from '../../styles/translationAdmin.module.css';

interface TranslationHomeProps {
  languages: AdminLanguage[];
  isManager: boolean;
  userEmail: string;
  /** Per-language counts; missing while loading, null if loading failed. */
  progress: Record<string, RowCounts | null | undefined>;
  onOpenLanguage: (code: string) => void;
  onManage: () => void;
}

const STEPS: { title: string; body: string }[] = [
  {
    title: 'Start with AI drafts',
    body: 'Every language begins with a computer-drafted translation of each piece of text on the site.',
  },
  {
    title: 'People review',
    body: 'Language admins — volunteers who speak the language — check each draft, fix it if needed, and approve it.',
  },
  {
    title: 'Mark ready',
    body: 'When every string is approved, a language admin marks the language ready to publish.',
  },
  {
    title: 'Publish',
    body: 'The site administrator publishes it, and it appears in the language menu for everyone. No site update needed.',
  },
];

const RoleCard: React.FC<{ languages: AdminLanguage[]; isManager: boolean; userEmail: string }> = ({
  languages,
  isManager,
  userEmail,
}) => {
  const mine = languages
    .filter((l) => isAssignedTo(l, userEmail, isManager))
    .map((l) => l.native_name || l.name);
  const names = joinNames(mine);

  if (isManager) {
    return (
      <div className={styles.roleCard}>
        <p>
          <strong>Your role: Translation manager</strong>
        </p>
        <p>
          You can add languages, choose language admins, and publish languages. You can also review and
          approve translations in any language.
        </p>
        {mine.length > 0 && <p>You're also a language admin for {names}.</p>}
      </div>
    );
  }

  return (
    <div className={styles.roleCard}>
      <p>
        <strong>Your role: Language admin for {names}</strong>
      </p>
      <p>
        {mine.length === 1
          ? `You review and approve the ${names} translations and mark ${names} ready to publish.`
          : 'You review and approve the translations for these languages and mark each one ready to publish.'}{' '}
        A translation manager then publishes it.
      </p>
    </div>
  );
};

const TranslationHome: React.FC<TranslationHomeProps> = ({
  languages,
  isManager,
  userEmail,
  progress,
  onOpenLanguage,
  onManage,
}) => (
  <div>
    <RoleCard languages={languages} isManager={isManager} userEmail={userEmail} />
    <div className={styles.card}>
      <p>
        Recovery Commons is written in English. Translations let members use the site in their own
        language.
      </p>
      <h2>How it works</h2>
      <ol className={styles.homeSteps}>
        {STEPS.map((step) => (
          <li key={step.title}>
            <strong>{step.title}</strong> — {step.body}
          </li>
        ))}
      </ol>
      <p className={styles.muted}>
        Until a language is published, only its language admins can see it — it's marked "preview" in
        their language menu.
      </p>
    </div>

    <div className={styles.card}>
      <h2>Languages</h2>
      {languages.length === 0 ? (
        <p className={styles.muted}>
          No languages yet.{isManager ? ' Add one under Manage languages below.' : ''}
        </p>
      ) : (
        languages.map((lang) => {
          const displayName = lang.native_name || lang.name;
          const counts = progress[lang.code];
          const pct = counts ? percentApproved(counts) : 0;
          const remaining = counts ? needsAttention(counts) : 0;
          return (
            <div key={lang.code} className={styles.homeLanguage}>
              <div className={styles.overviewTitle}>
                <h3>
                  {displayName} <span className={styles.muted}>({lang.code} · {lang.name})</span>
                </h3>
                <span className={styles[`badge_${lang.status}`]}>
                  {LANGUAGE_STATUS_LABELS[lang.status] || lang.status}
                </span>
              </div>
              {counts === undefined && <p className={styles.muted}>Loading progress…</p>}
              {counts === null && <p className={styles.muted}>Couldn't load progress.</p>}
              {counts && (
                <>
                  <div className={styles.progressBar}>
                    <div className={styles.progressFill} style={{ width: `${pct}%` }} />
                  </div>
                  <p className={styles.muted}>
                    {pct}% approved ·{' '}
                    {remaining === 0
                      ? 'nothing needs attention'
                      : `${remaining.toLocaleString()} need${remaining === 1 ? 's' : ''} attention`}
                  </p>
                </>
              )}
              {isManager && (
                <p className={styles.muted}>
                  Language admins: {lang.admins && lang.admins.length > 0 ? lang.admins.join(', ') : 'none yet'}
                </p>
              )}
              <div className={styles.homeLanguageActions}>
                <button type="button" className={styles.button} onClick={() => onOpenLanguage(lang.code)}>
                  Open {displayName} →
                </button>
              </div>
            </div>
          );
        })
      )}
    </div>

    {isManager && (
      <div className={styles.card}>
        <h2>Manage languages</h2>
        <p>Add a new language or change who the language admins are.</p>
        <div className={styles.actions}>
          <button type="button" className={styles.buttonSecondary} onClick={onManage}>
            Manage languages →
          </button>
        </div>
      </div>
    )}
  </div>
);

export default TranslationHome;
